import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MaxUint256 } from '@ethersproject/constants'
import IconLock from '~/assets/images/lock-white.svg'
import { QuestionIcon } from '~/components/icons'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import {
  useApproveCallback,
  useNeedReduceAllowance2Zero,
} from '~/hooks/useApproval'
import {
  useModalState,
  useModalToggle,
  useModalSwitch,
  useModalOpened,
  useApprovalModalStateDispatch,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useAllowanceTarget, useSpotSwapUserConfig } from '~/state/dex/hooks'
import TokenLogo from '~/components/TokenLogo'
import {
  StyledAllowanceArea,
  StyledTips,
  StyledInfo,
  StyledApproveBtn,
} from './styled'
import { captureExceptionWithLabel } from '~/utils/captureExceptionWithLabel'
import { BigNumber } from 'ethers'
import { message } from '~/components/Message'
import { useTokenAllowance } from '~/hooks/useTokenAllowance'
import { useAccount } from 'wagmi'
import { ApprovalState } from '~/hooks/useApproval'
import { useGA } from '~/hooks'

const AllowanceArea: React.FC<{
  token: SpotSwapToken
}> = ({ token }) => {
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const { isCrossSwap } = useSpotSwapUserConfig()
  const { trackClick } = useGA()

  const isApprovalModalOpen = useModalOpened(ApplicationModal.APPROVAL_STATUS)
  const isAllowanceModalOpened = useModalOpened(
    ApplicationModal.ALLOWANCE_TOKEN,
  )
  const switchAllowanceModal = useModalSwitch(ApplicationModal.ALLOWANCE_TOKEN)
  const allowanceTarget = useAllowanceTarget()
  const [approvalState, approve] = useApproveCallback(
    token,
    MaxUint256,
    allowanceTarget,
  )
  const [cancelApprovalState, cancelApprove] = useApproveCallback(
    token,
    BigNumber.from(0),
    allowanceTarget,
  )

  const needReduceAllowance2Zero = useNeedReduceAllowance2Zero(token)

  const allowance = useTokenAllowance(token, account, allowanceTarget)
  const isApproved = useMemo(() => allowance && !allowance.isZero(), [
    allowance,
  ])

  const i18nKey =
    approvalState === ApprovalState.APPROVED
      ? 'unApprove_token_to_tokenlon'
      : 'approve_token_to_tokenlon'

  const isApproveModalOpen = useModalOpened(ApplicationModal.APPROVAL_STATUS)
  const { progress } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleApprovalModal = useModalToggle(ApplicationModal.APPROVAL_STATUS, {
    stack: true,
    modalState: {
      title: t('token_approval', { symbol: token?.symbol }),
      description1: t(i18nKey, { symbol: token?.symbol }),
      description2: '',
      showLogo: false,
      token,
      approvalState,
      progress: ApprovalProgress.APPROVING,
    },
  })

  const toggleIntroModal = useModalToggle(ApplicationModal.PERMIT2_INTRO, {
    stack: true,
  })

  const toggleCancelApprovalModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        token,
        approvalState: cancelApprovalState,
        progress: ApprovalProgress.CANCEL_APPROVING,
      },
    },
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const handleApprove = async () => {
    const needCancelApprove = await needReduceAllowance2Zero()

    if (isApproved && needCancelApprove) {
      message.success(
        t('token_allowance_not_enough_and_need_cancel', {
          token: token.symbol,
        }),
      )

      toggleCancelApprovalModal()
      cancelApprove().catch((err) => {
        console.error(err)
        captureExceptionWithLabel('Exchange Cancel Approve', err)
      })
    } else {
      if (isApproved) {
        message.success(
          t('token_allowance_not_enough', { token: token.symbol }),
        )
      }
      if (!isApproveModalOpen) {
        updateApprovalState({
          progress: ApprovalProgress.APPROVING,
          hash: null,
        })
        toggleApprovalModal()
      }
      approve().catch((err) => {
        console.error(err)
        captureExceptionWithLabel('Exchange Approve', err)
      })
    }
  }

  useEffect(() => {
    if (
      progress === ApprovalProgress.APPROVE_SUCCESS &&
      isAllowanceModalOpened &&
      isApprovalModalOpen
    ) {
      trackClick('webtl_instant_approve', {
        symbol: token.symbol,
        is_success: true,
        is_cross_swap: isCrossSwap,
      })
      switchAllowanceModal(false, { stack: true })
    }
    if (
      progress === ApprovalProgress.APPROVE_REJECTED &&
      isAllowanceModalOpened
    ) {
      trackClick('webtl_instant_approve', {
        symbol: token.symbol,
        is_success: false,
        is_cross_swap: isCrossSwap,
      })
    }
  }, [progress])

  return (
    <StyledAllowanceArea>
      <div className="logo">
        <TokenLogo logo={token?.logo} symbol={token?.symbol} size={80} />
      </div>
      <span className="symbol">{token?.symbol}</span>
      <StyledInfo>{t('need_allowance', { symbol: token?.symbol })}</StyledInfo>
      <StyledTips>
        {t('why_need_set_allowance')}
        <QuestionIcon
          className="notice-info-logo"
          onClick={toggleIntroModal}
        />
      </StyledTips>
      <StyledApproveBtn onClick={handleApprove}>
        {t('tokenlon_set_allowance', { symbol: token?.symbol })}
        <img src={IconLock} />
      </StyledApproveBtn>
    </StyledAllowanceArea>
  )
}

export default AllowanceArea
